import React, { type Dispatch, type SetStateAction } from "react";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import CarSelection from "../../Forms/CarSelection";
import ItineraryPanelChromeRow from "../itineraryPanel/ItineraryPanelChromeRow";
import { type PanelResizeLayout } from "../panelResize";
import CarPriceDetail from "../pricing/CarPriceDetail";
import { type ItineraryPanelViewDirection } from "./ItineraryItemMotion";

interface CarDetailsState {
    "tollPrice":  number;
    "distance":   number;
    "totalPrice": number;
}

interface ItineraryCarSelectionPanelViewProps {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Whether prices display in EUR. */
    "isEuro": boolean;

    /** Shared resize layout measurements from Home. */
    "panelResizeLayout": PanelResizeLayout;

    /** MYCAR itinerary shown in chrome. */
    "ITakeMyCarData": ContainerProps["ITakeMyCarData"];

    /** Computed car toll/distance/total details. */
    "carDetails": CarDetailsState | null;

    /** Clears map leg selection when leaving car selection. */
    "handleResetMap": () => void;

    /** Resets the panel to the expanded snap tier. */
    "onResetPanelExpanded": () => void;

    /** Opens the car price detail sheet. */
    "onOpenCarPriceDetail": () => void;

    /** Closes the car price detail sheet. */
    "onCloseCarPriceDetail": () => void;

    /** Whether the car price detail sheet is open. */
    "isCarPriceOpen": boolean;

    /** Setter for backup return car routes. */
    "setBackupReturnItineraries": ContainerProps["setBackupReturnItineraries"];

    /** Setter for outbound car routes. */
    "setGoItineraries": ContainerProps["setGoItineraries"];

    /** Setter for MyCar selection mode. */
    "setITakeMyCar": Dispatch<SetStateAction<boolean>>;

    /** Setter for car itineraries used by chrome back navigation. */
    "setCarItineraries": ContainerProps["setCarItineraries"];

    /** Updates list↔details motion direction. */
    "setItineraryPanelViewDirection": Dispatch<SetStateAction<ItineraryPanelViewDirection>>;

    /** Setter for the selected car route id. */
    "setSelectedCarRouteId": ContainerProps["setSelectedCarRouteId"];

    /** Backup return car routes. */
    "backupReturnItineraries": ContainerProps["backupReturnItineraries"];

    /** Available car itineraries. */
    "carItineraries": ContainerProps["carItineraries"];

    /** Outbound car routes. */
    "goItineraries": ContainerProps["goItineraries"];

    /** Selected car route id. */
    "selectedCarRouteId": ContainerProps["selectedCarRouteId"];

    /** Planner search date used as car-selection source. */
    "date": ContainerProps["date"];

    /** Selected departure/arrival inputs. */
    "selectedInputs": ContainerProps["selectedInputs"];
}

/**
 * Presentational MyCar selection panel (chrome, route picker, price detail sheet).
 *
 * @param props Car selection panel composition props.
 * @returns Car selection panel layout root.
 */
const ItineraryCarSelectionPanelView: React.FC<ItineraryCarSelectionPanelViewProps> = ( {
    isDarkUi,
    isEuro,
    panelResizeLayout,
    ITakeMyCarData,
    carDetails,
    handleResetMap,
    onResetPanelExpanded,
    onOpenCarPriceDetail,
    onCloseCarPriceDetail,
    isCarPriceOpen,
    setBackupReturnItineraries,
    setGoItineraries,
    setITakeMyCar,
    setCarItineraries,
    setItineraryPanelViewDirection,
    setSelectedCarRouteId,
    backupReturnItineraries,
    carItineraries,
    goItineraries,
    selectedCarRouteId,
    date,
    selectedInputs,
} ) => {
    return (
        <div
            className="flex min-h-0 flex-1 flex-col"
            id="itinerary-car-selection-layout"
        >
            <ItineraryPanelChromeRow
                carItinerary={ITakeMyCarData}
                carTotalPriceChf={carDetails?.totalPrice ?? null}
                handleResetMap={handleResetMap}
                isDarkUi={isDarkUi}
                isEuro={isEuro}
                mode="carSelection"
                onOpenCarPriceDetail={onOpenCarPriceDetail}
                onResetPanelExpanded={onResetPanelExpanded}
                panelResizeLayout={panelResizeLayout}
                setBackupReturnItineraries={setBackupReturnItineraries}
                setGoItineraries={setGoItineraries}
                setITakeMyCar={setITakeMyCar}
                setItineraries={setCarItineraries}
                setItineraryPanelViewDirection={setItineraryPanelViewDirection}
                setSelectedCarRouteId={setSelectedCarRouteId}
            />
            <CarSelection
                backupReturnItineraries={backupReturnItineraries}
                carItineraries={carItineraries}
                goItineraries={goItineraries}
                isDarkUi={isDarkUi}
                selectedCarRouteId={selectedCarRouteId}
                setBackupReturnItineraries={setBackupReturnItineraries}
                setCarItineraries={setCarItineraries}
                setGoItineraries={setGoItineraries}
                setSelectedCarRouteId={setSelectedCarRouteId}
                sourceDate={date}
                sourceDestination={selectedInputs}
            />
            <CarPriceDetail
                distance={carDetails?.distance ?? 0}
                isDarkUi={isDarkUi}
                isEuro={isEuro}
                isOpen={isCarPriceOpen}
                onClose={onCloseCarPriceDetail}
                tollPrice={carDetails?.tollPrice ?? 0}
            />
        </div>
    );
};

export default ItineraryCarSelectionPanelView;
